import { yupResolver } from '@hookform/resolvers/yup';
import React, { forwardRef, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import ErrorMsg from 'src/components/errorMsg';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { RootState } from 'src/redux/store';
import * as yup from 'yup';

/**
 * Step 2 of /setup-account — onboarding "Shipping Profile" questionnaire.
 * Option values are the exact enum strings persisted on contacts_customers and
 * validated by the portal BE (validators/user.validator.ts). Keep both in sync.
 */

export const ERP_SYSTEM_OTHER = 'other';

export interface ShippingProfileFormData {
    erpSystem: string;
    erpSystemOther?: string;
    // No longer asked on this screen; retained so the submit payload keeps sending `false`.
    erpSyncInterest: boolean;
    monthlyShipmentVolume: string;
    shippingMode: string;
    laneRegularity: string;
    crossBorder: string;
}

interface ShippingProfileProps {
    onSubmit: (data: ShippingProfileFormData) => void;
    initialValues?: ShippingProfileFormData;
    isLoading: boolean;
    setIsFormValid?: (isValid: boolean) => void;
}

const I18N = 'SetupAccount.ShippingProfile';

const ERP_OPTION_KEYS = ['netsuite', 'sap', 'dynamics365', 'quickbooks', 'oracle', 'odoo', 'fishbowl', 'spreadsheets', 'other', 'none'] as const;
const VOLUME_OPTION_KEYS = ['1_5', '6_20', '21_50', '51_200', '200_plus'] as const;
const MODE_OPTION_KEYS = ['mostly_ltl', 'mostly_ftl', 'mix', 'occasional'] as const;
const LANE_OPTION_KEYS = ['mostly_regular', 'some_regular'] as const;
const CROSS_BORDER_OPTION_KEYS = ['no', 'sometimes'] as const;

// i18n option keys are Snake_Case; DB values stay lowercase.
const toI18nKey = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const buildOptions = (t: (key: string) => string, section: string, values: readonly string[]) => values.map((value) => ({ value, label: t(`${I18N}.${section}.Options.${toI18nKey(value)}`) }));

// Options can be removed over time (e.g. crossBorder 'yes' was dropped). A stale value saved on
// the contact record must not count as an answer, so every select field validates with oneOf.
const pickOne = (values: readonly string[], message: string) =>
    yup
        .string()
        .required(message)
        .oneOf([...values], message);

const schema = (t: (key: string) => string) =>
    yup.object().shape({
        erpSystem: pickOne(ERP_OPTION_KEYS, t(`${I18N}.Validation.Erp_Required`)),
        erpSystemOther: yup.string().when('erpSystem', {
            is: ERP_SYSTEM_OTHER,
            then: (s) =>
                s
                    .trim()
                    .required(t(`${I18N}.Validation.Erp_Other_Required`))
                    .max(255),
            otherwise: (s) => s.optional(),
        }),
        erpSyncInterest: yup.boolean().required(),
        monthlyShipmentVolume: pickOne(VOLUME_OPTION_KEYS, t(`${I18N}.Validation.Volume_Required`)),
        shippingMode: pickOne(MODE_OPTION_KEYS, t(`${I18N}.Validation.Mode_Required`)),
        laneRegularity: pickOne(LANE_OPTION_KEYS, t(`${I18N}.Validation.Lane_Required`)),
        crossBorder: pickOne(CROSS_BORDER_OPTION_KEYS, t(`${I18N}.Validation.Cross_Border_Required`)),
    });

// Prefill helper: only accept a stored value if it is still one of the offered options.
const knownOr = (values: readonly string[], value?: string | null) => (value && values.includes(value) ? value : '');

const emptyValues: ShippingProfileFormData = {
    erpSystem: '',
    erpSystemOther: '',
    erpSyncInterest: false,
    monthlyShipmentVolume: '',
    shippingMode: '',
    laneRegularity: '',
    crossBorder: '',
};

const ShippingProfile = forwardRef<HTMLFormElement, ShippingProfileProps>(({ onSubmit, initialValues, isLoading, setIsFormValid }, ref) => {
    const { t } = useTranslation();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    const erpOptions = buildOptions(t, 'Erp', ERP_OPTION_KEYS);
    const volumeOptions = buildOptions(t, 'Volume', VOLUME_OPTION_KEYS);
    const modeOptions = buildOptions(t, 'Mode', MODE_OPTION_KEYS);
    const laneOptions = buildOptions(t, 'Lane', LANE_OPTION_KEYS);
    const crossBorderOptions = buildOptions(t, 'Cross_Border', CROSS_BORDER_OPTION_KEYS);

    // Prefill priority: values kept by the parent (user pressed Back) > answers already
    // on the contact record (get-user-details returns the raw row) > empty.
    const defaultValues: ShippingProfileFormData = initialValues ?? {
        erpSystem: knownOr(ERP_OPTION_KEYS, currentUser?.erpSystem),
        erpSystemOther: currentUser?.erpSystemOther || '',
        // Not prefilled from the contact record — this screen no longer asks, so it always submits false.
        erpSyncInterest: false,
        monthlyShipmentVolume: knownOr(VOLUME_OPTION_KEYS, currentUser?.monthlyShipmentVolume),
        shippingMode: knownOr(MODE_OPTION_KEYS, currentUser?.shippingMode),
        laneRegularity: knownOr(LANE_OPTION_KEYS, currentUser?.laneRegularity),
        crossBorder: knownOr(CROSS_BORDER_OPTION_KEYS, currentUser?.crossBorder),
    };

    const { control, handleSubmit, watch } = useForm<ShippingProfileFormData>({
        resolver: yupResolver(schema(t)) as any,
        defaultValues: { ...emptyValues, ...defaultValues },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const values = watch();

    // Mirrors PersonalInfo: parent enables Continue only when every answer is present.
    useEffect(() => {
        if (!setIsFormValid) return;

        const otherOk = values.erpSystem !== ERP_SYSTEM_OTHER || Boolean(values.erpSystemOther?.trim());
        const isValid = Boolean(
            knownOr(ERP_OPTION_KEYS, values.erpSystem) &&
                otherOk &&
                knownOr(VOLUME_OPTION_KEYS, values.monthlyShipmentVolume) &&
                knownOr(MODE_OPTION_KEYS, values.shippingMode) &&
                knownOr(LANE_OPTION_KEYS, values.laneRegularity) &&
                knownOr(CROSS_BORDER_OPTION_KEYS, values.crossBorder)
        );

        setIsFormValid(isValid);
    }, [values, setIsFormValid]);

    const handleFormSubmit = (data: ShippingProfileFormData) => {
        onSubmit({
            ...data,
            erpSystemOther: data.erpSystem === ERP_SYSTEM_OTHER ? data.erpSystemOther?.trim() : '',
        });
    };

    // Single-select pill group — same shape as the quote flow's option pickers
    // (MyQuote/AddQuote/Steps/ShipmentDetails). type="button" matters: ButtonCmp defaults to submit.
    const renderPillGroup = (name: 'monthlyShipmentVolume' | 'laneRegularity' | 'crossBorder', label: string, options: { value: string; label: string }[]) => (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <div>
                    <p className="form_label block mb-1.5">
                        {label}
                        <span className="text-red-600 ms-[2px] leading-4">*</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {options.map((option) => (
                            <ButtonCmp
                                key={option.value}
                                type="button"
                                // Red border on every pill when the group failed validation (same color InputText uses).
                                className={`${field.value === option.value ? 'btn-outline-primary' : 'btn_secondary_black'} ${fieldState.error ? '!border-red-600' : ''}`}
                                onClick={() => field.onChange(option.value)}
                                disabled={isLoading}
                            >
                                {option.label}
                            </ButtonCmp>
                        ))}
                    </div>
                    <ErrorMsg errorText={fieldState.error?.message} />
                </div>
            )}
        />
    );

    return (
        <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            {/* 20px between question groups — the auth flow's standard form gap (Login, ForgotPassword, CustomerSelection all use gap-y-5) */}
            <div className="flex flex-col gap-5">
                {/* The ERP select and its "Other" free-text sit together with a tighter 10px gap (node 6330:127307) */}
                <div className="flex flex-col gap-2.5">
                    <Controller
                        name="erpSystem"
                        control={control}
                        render={({ field, fieldState }) => (
                            <SelectBox
                                label={t(`${I18N}.Erp.Label`)}
                                labelClassName="form_label block mb-1.5"
                                parentClassName="select-height-34"
                                value={erpOptions.find((option) => option.value === field.value) || null}
                                onChangeFunc={(selected: any) => field.onChange(!Array.isArray(selected) && selected?.value ? selected.value : '')}
                                options={erpOptions}
                                placeholder={t(`${I18N}.Erp.Placeholder`)}
                                errorText={Boolean(fieldState.error?.message)}
                                isDisabled={isLoading}
                                required
                            />
                        )}
                    />

                    {values.erpSystem === ERP_SYSTEM_OTHER && (
                        <Controller
                            name="erpSystemOther"
                            control={control}
                            render={({ field, fieldState }) => (
                                // Unlabelled by design — the placeholder carries the hint (node 6330:127313)
                                <InputText
                                    inputName="erpSystemOther"
                                    placeholder={t(`${I18N}.Erp.Other_Placeholder`)}
                                    value={field.value}
                                    onChangeFunc={(e) => field.onChange(e.target.value)}
                                    onBlur={field.onBlur}
                                    errorText={Boolean(fieldState.error?.message)}
                                    disabled={isLoading}
                                    maxLength={255}
                                />
                            )}
                        />
                    )}
                </div>

                {renderPillGroup('monthlyShipmentVolume', t(`${I18N}.Volume.Label`), volumeOptions)}

                <Controller
                    name="shippingMode"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SelectBox
                            label={t(`${I18N}.Mode.Label`)}
                            labelClassName="form_label block mb-1.5"
                            parentClassName="select-height-34"
                            value={modeOptions.find((option) => option.value === field.value) || null}
                            onChangeFunc={(selected: any) => field.onChange(!Array.isArray(selected) && selected?.value ? selected.value : '')}
                            options={modeOptions}
                            placeholder={t(`${I18N}.Mode.Placeholder`)}
                            errorText={Boolean(fieldState.error?.message)}
                            isDisabled={isLoading}
                            required
                        />
                    )}
                />

                {renderPillGroup('crossBorder', t(`${I18N}.Cross_Border.Label`), crossBorderOptions)}
                {renderPillGroup('laneRegularity', t(`${I18N}.Lane.Label`), laneOptions)}
            </div>
        </form>
    );
});

ShippingProfile.displayName = 'ShippingProfile';

export default ShippingProfile;
